import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';

import { Button, Flex, Form, Select } from 'antd';

import dayjs from 'dayjs';

import axios from '../../../../axios';

import { getFilterOptions, sortDates, sortStrings } from '../../../../helpers/filter';
import { hasPermission } from '../../../../helpers/user.js';

import { DATE_FORMAT, EDIT_COMPONENT_TYPE } from '../../../../constants/common';
import { BLANK_FILTER_TEXT } from '../../../../constants/livestock';
import messages from '../../../../constants/messages';
import { PERMISSIONS } from '../../../../constants/permissions.js';
import { TABLE_CONFIG_COLUMNS, TABLE_CONFIG_KEYS } from '../../../../constants/table-configurations';
import { TAG_CSV_TEMPLATE } from '../../../../constants/templates';
import { TAGS_CSV_VALIDATION_SCHEMA } from '../../../../constants/validation-schemas';

import { useModal } from '../../../../hooks/useModal';
import { useUnsavedChangesPrompt } from '../../../../hooks/useUnsavedChangesPrompt';

import { fetchFarms } from '../../../../redux/actions/farm';
import { createTag, deleteTags, fetchAllTags, searchTags } from '../../../../redux/actions/tag';
import { setSelectedFilters } from '../../../../redux/reducers/filters';

import AppModal from '../../../../components/AppModal';
import AppTable from '../../../../components/AppTable';
import { COLUMN_SIZE } from '../../../../components/AppTable/constants';
import AddTagModal from '../../../../components/Modals/AddTagModal';
import UploadModal from '../../../../components/Modals/UploadModal';
import { errorToastHandler, successToastHandler, warnToastHandler } from '../../../../components/action_notifier';
import UnsavedChangesModal from '../../../../components/ui/UnsavedChangesModal';

import { TagService } from '../../../../services';

const commonColumnProperties = {
  ellipsis: true,
  sortDirections: ['ascend', 'descend']
};

function Tags() {
  const dispatch = useDispatch();
  const history = useHistory();
  const formRef = useRef(null);
  const formCreateRef = useRef(null);

  const { options: farm_opts, loading: farmsLoading } = useSelector(state => state.farm);
  const { permissions } = useSelector(state => state.user);
  const { tags, loading: tagsLoading } = useSelector(state => state.tag);
  const { selectedFilters } = useSelector(state => state.filters);

  const [createFormSubmittable, setCreateFormSubmittable] = useState(false);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [submittable, setSubmittable] = useState(false);
  const [state, setState] = useState({
    add_modal: false,
    delete_modal: false,
    farmFilterOptions: [],
    selected_tags: [],
    uploadSuccessMessage: ''
  });

  const [uploadModalVisible, openUploadModal, closeUploadModal] = useModal();
  const [uploadSuccessModalVisible, openUploadSuccessModal, closeUploadSuccessModal] = useModal();

  useEffect(() => {
    getTags();
    getFarms();

    return () => {
      dispatch(setSelectedFilters({}));
    };
  }, []);

  useEffect(() => {
    if (farm_opts?.length) {
      setState(prevState => ({ ...prevState, csv_farm: farm_opts?.at(0) }));
    }
  }, [farm_opts]);

  const selectRecords = useCallback(
    recordIds => {
      setState(prevState => ({ ...prevState, selected_tags: recordIds }));
    },
    [state.selected_tags]
  );

  const setFilters = useCallback(
    (pagination, filters) => {
      setFilteredInfo(filters);
      dispatch(setSelectedFilters(filters));
    },
    [filteredInfo]
  );

  const clearFilters = () => {
    setFilteredInfo({});
    dispatch(setSelectedFilters({}));
  };

  async function getTags() {
    try {
      const response = await dispatch(fetchAllTags()).unwrap();

      const farmFilterOptions = getFilterOptions(response, 'farm', 'name');
      setState(prevState => ({ ...prevState, farmFilterOptions }));
    } catch (error) {
      errorToastHandler(error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  }

  async function getFarms() {
    try {
      await dispatch(fetchFarms({ all: true })).unwrap();
    } catch (error) {
      errorToastHandler(error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  }

  async function getTagsBySearchQuery(searchQuery) {
    try {
      await dispatch(searchTags({ query: searchQuery })).unwrap();
    } catch (error) {
      errorToastHandler(error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  }

  async function addTag() {
    const tagFormValues = await formCreateRef.current?.validateFields();

    try {
      await dispatch(createTag(tagFormValues)).unwrap();
      await getTags();
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_CREATE_DATA);
    }

    await toggleModal('add_modal');
  }

  async function toggleModal(modal) {
    await formCreateRef.current?.resetFields();
    setState(prevState => ({ ...prevState, [modal]: !prevState[modal] }));
  }

  async function deleteSelectedTags() {
    await dispatch(deleteTags(state.selected_tags)).unwrap();
    await toggleModal('delete_modal');
    setState(prevState => ({ ...prevState, selected_tags: [] }));
  }

  const handleCSVFarmChange = (_, selectedOption) => {
    setState(prevState => ({ ...prevState, csv_farm: selectedOption }));
  };

  async function uploadCSV(csv) {
    try {
      const response = await axios.post('tags/create/csv', { csv, farm_id: state.csv_farm?.id });

      if (response?.status === 200) {
        setState(prevState => ({ ...prevState, uploadSuccessMessage: response?.data?.message }));
        await getTags();
        closeUploadModal();
        openUploadSuccessModal();
      }
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.DEFAULT_ERROR);
    }
  }

  async function handleChangeTagFarm(updatedTag) {
    const tag = updatedTag?.at(0);
    const payload = { tags: [{ id: tag?.id, farm_id: tag?.farm?.id }] };

    try {
      await TagService.updateMany(payload);

      successToastHandler(messages.UPDATED);
    } catch (error) {
      errorToastHandler(messages.DEFAULT_ERROR);
    }
  }

  const actions = useMemo(() => {
    const isAnyTagSelected = state.selected_tags?.length;

    return [
      {
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={isAnyTagSelected || !hasPermission([PERMISSIONS.TAGS.CREATE], permissions)}
            onClick={() => toggleModal('add_modal')}>
            Add Kraal Tag
          </Button>
        )
      },
      {
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!isAnyTagSelected || !hasPermission([PERMISSIONS.TAGS.DELETE_ALL], permissions)}
            onClick={() => toggleModal('delete_modal')}>
            Archive Kraal Tags
          </Button>
        )
      },
      {
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!hasPermission([PERMISSIONS.TAGS.CREATE], permissions)}
            onClick={openUploadModal}>
            Upload Kraal Tag list
          </Button>
        )
      },
      {
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!Object.values(selectedFilters)?.length}
            onClick={clearFilters}>
            Clear all filters
          </Button>
        )
      }
    ];
  }, [selectedFilters, state.selected_tags, permissions]);

  const columns = [
    {
      ...commonColumnProperties,
      title: 'Kraal Tag ID',
      dataIndex: 'diagri_id',
      configColumnKey: TABLE_CONFIG_COLUMNS.KRAAL_TAG_ID,
      filteredValue: filteredInfo.diagri_id || null,
      fixed: 'left',
      searchable: true,
      width: COLUMN_SIZE.XL,
      render: (value, record) => <Link to={`/administration/tag/${record.id}`}>{record.diagri_id || '/'}</Link>,
      sorter: (a, b) => sortStrings(a.diagri_id, b.diagri_id)
    },
    {
      ...commonColumnProperties,
      title: 'Identifier',
      dataIndex: 'identifier',
      configColumnKey: TABLE_CONFIG_COLUMNS.ID,
      filteredValue: filteredInfo.identifier || null,
      searchable: true,
      width: COLUMN_SIZE.MD,
      render: (value, record) => <Link to={`/administration/tag/${record.id}`}>{value}</Link>,
      sorter: (a, b) => sortStrings(a.identifier, b.identifier)
    },
    {
      ...commonColumnProperties,
      title: 'Farm ID',
      dataIndex: 'farm_identifier',
      configColumnKey: TABLE_CONFIG_COLUMNS.FARM_IDENTIFIER,
      filteredValue: filteredInfo.farm_identifier || null,
      searchable: true,
      width: COLUMN_SIZE.MD,
      render: (value, record) => <Link to={`/farms/${record.farm?.id}`}>{record.farm?.identifier}</Link>,
      sorter: (a, b) => sortStrings(a.farm?.identifier, b.farm?.identifier)
    },
    {
      ...commonColumnProperties,
      title: 'Farm Name',
      dataIndex: 'farm',
      configColumnKey: TABLE_CONFIG_COLUMNS.FARM,
      editConfig: {
        type: EDIT_COMPONENT_TYPE.SELECT,
        options: farm_opts,
        valueKey: 'id'
      },
      filterSearch: true,
      filteredValue: filteredInfo.farm || null,
      filters: state.farmFilterOptions || [],
      width: COLUMN_SIZE.XL,
      onFilter: (value, record) => (value === BLANK_FILTER_TEXT ? !record?.farm?.name : record?.farm?.name === value),
      sorter: (a, b) => sortStrings(a.farm?.name, b.farm?.name),
      render: (value, record) => value?.name ?? ''
    },
    {
      ...commonColumnProperties,
      title: 'Gateway ID',
      dataIndex: 'gateway_identifier',
      configColumnKey: TABLE_CONFIG_COLUMNS.GATEWAY_IDENTIFIER,
      filteredValue: filteredInfo.gateway_identifier || null,
      searchable: true,
      width: COLUMN_SIZE.MD,
      render: (value, record) => (
        <Link to={`/administration/gateway/${record.gateway?.id}`}>{record.gateway?.identifier}</Link>
      ),
      sorter: (a, b) => sortStrings(a.gateway?.identifier, b.gateway?.identifier)
    },
    {
      ...commonColumnProperties,
      title: 'Gateway Name',
      dataIndex: 'gateway_name',
      configColumnKey: TABLE_CONFIG_COLUMNS.GATEWAY_NAME,
      filteredValue: filteredInfo.gateway_name || null,
      searchable: true,
      width: COLUMN_SIZE.XXL,
      render: (value, record) => record?.gateway?.name ?? '',
      sorter: (a, b) => sortStrings(a.gateway?.name, b.gateway?.name)
    },
    {
      ...commonColumnProperties,
      title: 'Type',
      dataIndex: 'type',
      configColumnKey: TABLE_CONFIG_COLUMNS.TYPE,
      filteredValue: filteredInfo.type || null,
      searchable: true,
      width: COLUMN_SIZE.SM,
      sorter: (a, b) => sortStrings(a.type, b.type)
    },
    {
      ...commonColumnProperties,
      title: 'Deveui',
      dataIndex: 'deveui',
      configColumnKey: TABLE_CONFIG_COLUMNS.DEVEUI,
      filteredValue: filteredInfo.deveui || null,
      searchable: true,
      width: COLUMN_SIZE.XXL,
      sorter: (a, b) => sortStrings(a.deveui, b.deveui)
    },
    {
      ...commonColumnProperties,
      title: 'Version',
      dataIndex: 'version',
      configColumnKey: TABLE_CONFIG_COLUMNS.VERSION,
      filteredValue: filteredInfo.version || null,
      searchable: true,
      width: COLUMN_SIZE.MD,
      sorter: (a, b) => sortStrings(a.version, b.version)
    },
    {
      ...commonColumnProperties,
      title: 'Firmware Version',
      dataIndex: 'firmware_version',
      configColumnKey: TABLE_CONFIG_COLUMNS.FIRMWARE_VERSION,
      filteredValue: filteredInfo.firmware_version || null,
      searchable: true,
      width: COLUMN_SIZE.MD,
      sorter: (a, b) => sortStrings(a.firmware_version, b.firmware_version)
    },
    {
      ...commonColumnProperties,
      title: 'Creation At',
      dataIndex: 'created_at',
      configColumnKey: TABLE_CONFIG_COLUMNS.CREATED_AT,
      filteredValue: filteredInfo.created_at || null,
      searchable: true,
      width: COLUMN_SIZE.XL,
      render: value => (value ? dayjs(value).format(DATE_FORMAT.DATETIME) : null),
      sorter: (a, b) => sortDates(a.created_at, b.created_at)
    },
    {
      ...commonColumnProperties,
      title: 'Updated At',
      dataIndex: 'updated_at',
      configColumnKey: TABLE_CONFIG_COLUMNS.UPDATED_AT,
      filteredValue: filteredInfo.updated_at || null,
      searchable: true,
      width: COLUMN_SIZE.XL,
      render: value => (value ? dayjs(value).format(DATE_FORMAT.DATETIME) : null),
      sorter: (a, b) => sortDates(a.updated_at, b.updated_at)
    }
  ];

  const { NavigationPrompt, modalProps, nextLocation, confirmed, triggerUnsavedChangesPrompt } =
    useUnsavedChangesPrompt({
      when: isFormDirty,
      onSaveAndLeave: async () => {
        if (createFormSubmittable) {
          await addTag();

          return true;
        } else {
          warnToastHandler(messages.DATA_REQUIRED);
          formCreateRef.current?.validateFields();

          return false;
        }
      },
      onDiscardAndLeave: async () => {
        setIsFormDirty(false);
        await toggleModal('add_modal');
      }
    });

  useEffect(() => {
    if (!isFormDirty && nextLocation) {
      history.push(`${nextLocation.pathname}${nextLocation.search}`);
    }
  }, [isFormDirty, nextLocation, history]);

  const handleCancel = useCallback(() => {
    isFormDirty ? triggerUnsavedChangesPrompt() : toggleModal('add_modal');
  }, [isFormDirty]);

  return (
    <>
      {NavigationPrompt}
      <AppTable
        headerClass="py-2"
        searchPlaceholder="Search Kraal tags"
        actions={actions}
        baseColumns={columns}
        dataSource={tags}
        filterable={false}
        loading={farmsLoading || tagsLoading}
        tableConfigKey={TABLE_CONFIG_KEYS.ADMIN_TAGS}
        handleOnChange={setFilters}
        handleOnSelectRecords={selectRecords}
        handleSearchChange={getTagsBySearchQuery}
        updateTableData={handleChangeTagFarm}
      />

      <AppModal
        isOpen={state.delete_modal}
        confirmButtonColor="danger"
        confirmButtonText="Archive"
        title="Archive tags"
        handleCancel={() => toggleModal('delete_modal')}
        handleConfirm={deleteSelectedTags}>
        <div className="py-4">{messages.ARCHIVE_CONFIRMATION('selected tags')}</div>
      </AppModal>

      <AddTagModal
        formRef={formCreateRef}
        isOpen={state.add_modal}
        farm_opts={farm_opts}
        submittable={createFormSubmittable}
        onCancel={handleCancel}
        onSubmit={addTag}
        setCreateFormSubmittable={setCreateFormSubmittable}
      />

      <UploadModal
        fileName="tags-template.csv"
        formInitValues={{ farm: state.csv_farm }}
        formRef={formRef}
        isOpen={uploadModalVisible}
        schema={TAGS_CSV_VALIDATION_SCHEMA}
        submittable={submittable}
        template={TAG_CSV_TEMPLATE}
        onCancel={closeUploadModal}
        onUpload={uploadCSV}
        setSubmittable={setSubmittable}>
        <Form.Item
          label="Select farm"
          name="farm"
          colon={false}
          labelAlign="left"
          rules={[{ required: true, message: messages.FIELD_REQUIRED('Farm') }]}>
          <Select
            placeholder="Select a related farm"
            className="w-full"
            options={farm_opts}
            onChange={handleCSVFarmChange}
          />
        </Form.Item>
      </UploadModal>

      <AppModal
        confirmButtonText="Ok"
        cancelButtonHidden={true}
        isOpen={uploadSuccessModalVisible}
        handleConfirm={closeUploadSuccessModal}>
        <Flex align="center" justify="center" className="pb-2 pt-5">
          {state.uploadSuccessMessage}
        </Flex>
      </AppModal>

      <UnsavedChangesModal
        isOpen={modalProps.visible}
        handleCancel={modalProps.onCancel}
        handleConfirm={modalProps.onSave}
        handleDiscard={modalProps.onDiscard}
      />
    </>
  );
}

export default memo(Tags);
